import React from 'react';
import { Tooltip } from 'antd';

/**
 * Hosts a `MessageMenu` on the bubble it belongs to.
 *
 * The four panes (direct, group, and each of their thread streams) each wrote
 * this same antd Tooltip by hand, which is how the popup's overflow behaviour
 * came to be wrong in six places at once.
 *
 * WHY the overflow options are spelled out
 * ----------------------------------------
 * antd derives a placement's overflow config in `_util/placements.js`, and its
 * `switch` only names the four base placements — `top`, `bottom`, `left`,
 * `right`. The corner placements this menu uses (`rightBottom`, `leftBottom`)
 * fall through it, so they end up with `adjustX/adjustY` and NO `shiftX/shiftY`:
 * flipping to the opposite side is then the only correction available.
 *
 * On a phone that correction cannot help. The bubble is 86% of the viewport
 * (`.message-width`), so neither side has room for a 168px menu — rc-align
 * tries the flip, finds it no better, and leaves the popup where it was, hanging
 * off the screen edge. A long message made it obvious because a tall bubble
 * pushes the menu to a corner with even less clearance.
 *
 * `shiftX`/`shiftY` add the missing correction: slide the popup back inside the
 * visible region instead of only flipping it. The menu then overlaps the bubble
 * on a narrow screen, which is what an overlay menu is for.
 *
 * Passed through `autoAdjustOverflow` rather than `align`, because antd merges
 * this into the per-placement defaults while `align` would replace the whole
 * overflow object. Module-level constant, not an inline literal: antd memoises
 * the placement table without this in its dependency list, so a fresh object per
 * render would be silently ignored after the first.
 */
const OVERFLOW = { adjustX: true, adjustY: true, shiftX: true, shiftY: true };

const MessageMenuTooltip = ({ menu, placement = 'rightBottom', children }) => (
  <Tooltip color="none" placement={placement} autoAdjustOverflow={OVERFLOW} title={menu}>
    {children}
  </Tooltip>
);

export default MessageMenuTooltip;
